/**
 * A case's tasks — its workflow checklist and whatever the firm added (FR-E4).
 * Split out of `CaseDetail.tsx` in Wave 15C; the verbs are unchanged: ticking
 * one needs `task:can_update`, adding one `task:can_create`.
 *
 * A required task is the workflow's own, and a case cannot leave its stage
 * while one is open — the stage section names them; this list says which are
 * which. A task's status (to do, due today, overdue, done) comes from
 * `taskStatus` and its colour from the one map.
 */

import { Alert, Button, Checkbox, EmptyState, Input, formatDate } from '@fiveox/ui'
import { ListChecks, Plus } from 'lucide-react'
import { useState } from 'react'

import { RecordSection, StatusBadge, taskStatus } from '../../components/page'
import { type CaseTask, cases } from '../../lib/api/cases'
import { todayIso, useAction } from './useAction'

export function CaseTasks({
  caseId,
  tasks,
  canUpdate,
  canCreate,
  onChanged,
}: {
  caseId: string
  tasks: CaseTask[]
  canUpdate: boolean
  canCreate: boolean
  onChanged: () => Promise<void> | void
}) {
  const [title, setTitle] = useState('')
  const { error, busy, run } = useAction()
  const today = todayIso()
  const open = tasks.filter((task) => !task.is_done).length

  const tick = (task: CaseTask, done: boolean) =>
    run(async () => {
      await cases.setTask(task.id, { is_done: done })
      await onChanged()
    }, 'The task could not be updated.')

  const add = () =>
    run(async () => {
      await cases.addTask(caseId, { title: title.trim() })
      setTitle('')
      await onChanged()
    }, 'The task could not be added.')

  return (
    <RecordSection
      id="case-tasks"
      icon={<ListChecks size={18} aria-hidden />}
      title="Tasks"
      description={
        tasks.length === 0
          ? 'Nothing to do on this case yet.'
          : open === 0
            ? 'Everything on this case is done.'
            : `${open} of ${tasks.length} still to do.`
      }
    >
      <div className="space-y-4">
        {error && <Alert tone="error">{error}</Alert>}

        {tasks.length === 0 ? (
          <EmptyState
            variant="outline"
            title="No tasks"
            description="The workflow adds its checklist as the case moves. Add your own below."
          />
        ) : (
          <ul className="divide-border divide-y">
            {tasks.map((task) => {
              const status = taskStatus(task, today)
              return (
                <li key={task.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                  <Checkbox
                    aria-label={`${task.title} — done`}
                    checked={task.is_done}
                    disabled={!canUpdate || busy}
                    onChange={(e) => void tick(task, e.target.checked)}
                    className="mt-1"
                  />
                  <div className="min-w-0 flex-1">
                    <p
                      className={
                        task.is_done
                          ? 'text-muted-foreground text-sm line-through'
                          : 'text-foreground text-sm font-medium'
                      }
                    >
                      {task.title}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {[
                        task.is_required ? 'Required by the workflow' : 'Added by the firm',
                        task.stage_key,
                        task.role,
                        task.due_date ? `due ${formatDate(task.due_date)}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <StatusBadge kind="task" status={status} />
                </li>
              )
            })}
          </ul>
        )}

        {canCreate && (
          <form
            className="border-border flex flex-col gap-2 border-t pt-4 sm:flex-row sm:items-start"
            onSubmit={(event) => {
              event.preventDefault()
              if (title.trim()) void add()
            }}
          >
            <div className="min-w-0 flex-1">
              <Input
                size="sm"
                aria-label="A new task"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Add a task — what needs doing?"
              />
            </div>
            <Button type="submit" size="sm" disabled={!title.trim() || busy}>
              <Plus size={16} aria-hidden /> Add task
            </Button>
          </form>
        )}
      </div>
    </RecordSection>
  )
}
